/**
 * Google Analytics 4 (web) — pendant du `google.ts` mobile, mais via gtag.js
 * au lieu du SDK Firebase natif. Le script gtag est chargé par
 * `AnalyticsProvider` (next/script) ; ce module se contente de pousser des
 * events dans `window.gtag`.
 *
 * Désactivé tant que `NEXT_PUBLIC_GA_MEASUREMENT_ID` n'est pas renseigné :
 * toutes les fonctions deviennent alors des no-op silencieux.
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 */

export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID ?? "";

const FORCE_ENABLE = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === "true";

/** Activé en production (ou en local si le flag debug est posé) et si un ID existe. */
export const GA_ENABLED =
  !!GA_MEASUREMENT_ID &&
  (process.env.NODE_ENV === "production" || FORCE_ENABLE);

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/** Pousse une commande gtag si le SDK est prêt. Silencieux sinon. */
function gtag(...args: unknown[]): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag(...args);
}

// ─── Types GA4 ───────────────────────────────────────────────────────────────

/**
 * Article GA4 (utilisé dans tous les events e-commerce).
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_to_cart_item
 */
export interface GA4Item {
  /** item_id OU item_name obligatoire. */
  item_id?: string;
  item_name?: string;
  affiliation?: string;
  coupon?: string;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  [key: string]: unknown;
}

// ─── Identification ───────────────────────────────────────────────────────────

/** Définit (ou efface) le user_id GA4 — appliqué aux events suivants. */
export function setGoogleUserId(userId: string | null): void {
  if (!GA_ENABLED) return;
  gtag("set", { user_id: userId ?? null });
}

/** User properties custom GA4. */
export function setGoogleUserProperties(
  properties: Record<string, string | null>
): void {
  if (!GA_ENABLED) return;
  gtag("set", "user_properties", properties);
}

// ─── Events génériques ────────────────────────────────────────────────────────

/** Event GA4 générique (login, sign_up, search, etc.). */
export function logGoogleEvent(
  eventName: string,
  parameters?: Record<string, unknown>
): void {
  if (!GA_ENABLED) return;
  gtag("event", eventName, parameters ?? {});
}

/** Pageview manuel (SPA) — l'auto-collection initiale est gérée par gtag config. */
export function logGooglePageView(path: string, title?: string): void {
  if (!GA_ENABLED || typeof window === "undefined") return;
  gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
}

// ─── Events "Ventes en ligne" (spec GA4) ─────────────────────────────────────

export function logGoogleAddToCart(params: {
  currency: string;
  value: number;
  items: GA4Item[];
}): void {
  if (!GA_ENABLED) return;
  gtag("event", "add_to_cart", params);
}

export function logGoogleRemoveFromCart(params: {
  currency: string;
  value: number;
  items: GA4Item[];
}): void {
  if (!GA_ENABLED) return;
  gtag("event", "remove_from_cart", params);
}

export function logGoogleViewItem(params: {
  currency: string;
  value: number;
  items: GA4Item[];
}): void {
  if (!GA_ENABLED) return;
  gtag("event", "view_item", params);
}

export function logGoogleViewItemList(params: {
  item_list_id?: string;
  item_list_name?: string;
  items: GA4Item[];
}): void {
  if (!GA_ENABLED) return;
  gtag("event", "view_item_list", params);
}

export function logGoogleSelectItem(params: {
  item_list_id?: string;
  item_list_name?: string;
  items: GA4Item[];
}): void {
  if (!GA_ENABLED) return;
  gtag("event", "select_item", params);
}

export function logGoogleViewCart(params: {
  currency: string;
  value: number;
  items: GA4Item[];
}): void {
  if (!GA_ENABLED) return;
  gtag("event", "view_cart", params);
}

export function logGoogleBeginCheckout(params: {
  currency: string;
  value: number;
  coupon?: string;
  items: GA4Item[];
}): void {
  if (!GA_ENABLED) return;
  gtag("event", "begin_checkout", params);
}

export function logGoogleAddShippingInfo(params: {
  currency: string;
  value: number;
  coupon?: string;
  shipping_tier?: string;
  items: GA4Item[];
}): void {
  if (!GA_ENABLED) return;
  gtag("event", "add_shipping_info", params);
}

export function logGoogleAddPaymentInfo(params: {
  currency: string;
  value: number;
  coupon?: string;
  payment_type?: string;
  items: GA4Item[];
}): void {
  if (!GA_ENABLED) return;
  gtag("event", "add_payment_info", params);
}

/**
 * Achat. `transaction_id` doit être unique pour éviter les doublons.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase
 */
export function logGooglePurchase(params: {
  transaction_id: string;
  currency: string;
  value: number;
  items: GA4Item[];
  coupon?: string;
  shipping?: number;
  tax?: number;
}): void {
  if (!GA_ENABLED) return;
  gtag("event", "purchase", params);
}

export function logGoogleRefund(params: {
  transaction_id: string;
  currency: string;
  value: number;
  items?: GA4Item[];
}): void {
  if (!GA_ENABLED) return;
  gtag("event", "refund", params);
}

export function logGoogleAddToWishlist(params: {
  currency: string;
  value: number;
  items: GA4Item[];
}): void {
  if (!GA_ENABLED) return;
  gtag("event", "add_to_wishlist", params);
}
